import type { TopLevelSpec } from "vega-lite";
import type { BaseChartSpecOptions, FieldType } from "../types.js";
import {
  numberFormatConfig,
  numberFormatProps,
  type NumberFormat,
} from "./format.js";
import { buildTitle, fieldRef, inlineData } from "./shared.js";

export interface ScatterChartSpecOptions extends BaseChartSpecOptions {
  /** Field for the x axis. */
  x: string;
  /** Field for the y axis. */
  y: string;
  xType?: FieldType;
  yType?: FieldType;
  /** Field that colors each point by category. */
  color?: string;
  colorType?: FieldType;
  /** Field that sizes each point (bubble chart). */
  size?: string;
  sizeType?: FieldType;
  xTitle?: string;
  yTitle?: string;
  /** How the x values read on their axis and the tooltip. */
  xFormat?: NumberFormat;
  /** How the y values read on their axis and the tooltip. */
  yFormat?: NumberFormat;
  /** How the x values read on their axis, when terser than `xFormat`. */
  xAxisFormat?: NumberFormat;
  /** How the y values read on their axis, when terser than `yFormat`. */
  yAxisFormat?: NumberFormat;
  /** Gridlines on both axes. On by default. */
  grid?: boolean;
}

/** Builds a Vega-Lite scatter / bubble chart spec. */
export function scatterChartSpec(
  options: ScatterChartSpecOptions,
): TopLevelSpec {
  const {
    data,
    x,
    y,
    color,
    colorType = "nominal",
    size,
    sizeType = "quantitative",
    xType = "quantitative",
    yType = "quantitative",
    xTitle,
    yTitle,
    title,
    subtitle,
    xFormat,
    yFormat,
    xAxisFormat = xFormat,
    yAxisFormat = yFormat,
    grid = true,
  } = options;

  const titleValue = buildTitle(title, subtitle);
  const xEnc = {
    field: fieldRef(x),
    type: xType,
    ...(xTitle !== undefined ? { title: xTitle } : {}),
    axis: { grid, ...numberFormatProps(xAxisFormat) },
  };
  const yEnc = {
    field: fieldRef(y),
    type: yType,
    ...(yTitle !== undefined ? { title: yTitle } : {}),
    axis: { grid, ...numberFormatProps(yAxisFormat) },
  };
  const colorField = color ? { field: fieldRef(color), type: colorType } : null;
  const sizeField = size ? { field: fieldRef(size), type: sizeType } : null;

  return {
    data: inlineData(data),
    // Opaque: Vega-Lite's default 0.7 opacity dims a point toward the page, and
    // the dark-mode chart colors have little contrast to spare.
    mark: { type: "point", filled: true, opacity: 1 },
    encoding: {
      x: xEnc,
      y: yEnc,
      ...(colorField ? { color: colorField } : {}),
      ...(sizeField ? { size: sizeField } : {}),
      tooltip: [
        {
          field: xEnc.field,
          type: xEnc.type,
          title: xEnc.title,
          ...numberFormatProps(xFormat),
        },
        {
          field: yEnc.field,
          type: yEnc.type,
          title: yEnc.title,
          ...numberFormatProps(yFormat),
        },
        ...(colorField ? [colorField] : []),
        ...(sizeField ? [sizeField] : []),
      ],
    },
    ...(titleValue !== undefined ? { title: titleValue } : {}),
    ...numberFormatConfig(xFormat, yFormat, xAxisFormat, yAxisFormat),
  };
}
